// Passerelle Drive — accès aux API serveur (/api/passerelle).
// La source de vérité est SERVEUR : aucune de ces données ne transite par le
// cache localStorage de `store.js`, aucune sauvegarde historique ne peut donc
// écraser une pièce importée ni contourner l'anti-doublon.
const API = `${process.env.REACT_APP_BACKEND_URL}/api/passerelle`;

const json = async (r) => {
  const data = await r.json().catch(() => ({}));
  if (!r.ok) return { error: data.detail || `Erreur ${r.status}` };
  return { error: null, data };
};

// Chargement explicite : l'appelant distingue « aucune donnée » d'« erreur serveur »
// (une table vide et une lecture en échec ne se ressemblent plus à l'écran).
export const chargerRessource = (chemin) =>
  fetch(`${API}/${chemin}`)
    .then(json)
    .catch(() => ({ error: 'serveur injoignable' }));

export const fetchEtatPasserelle = async () => (await json(await fetch(`${API}/etat`))).data ?? null;
export const fetchDriveConnexion = async () => (await json(await fetch(`${API}/drive/connexion`))).data ?? null;

export const majDriveConnexion = (payload) =>
  fetch(`${API}/drive/connexion`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }).then(json);

export const lancerSyncDrive = () => fetch(`${API}/drive/sync`, { method: 'POST' }).then(json);

export const deposerDocument = (fichier, { canal = 'web', deposant = 'gérant' } = {}) => {
  const body = new FormData();
  body.append('fichier', fichier);
  body.append('canal', canal);
  body.append('deposant', deposant);
  return fetch(`${API}/depot`, { method: 'POST', body }).then(json);
};

export const fetchDocumentsPasserelle = async (etat) =>
  (await json(await fetch(`${API}/documents${etat ? `?etat=${etat}` : ''}`))).data ?? [];
export const fetchDocumentPasserelle = async (id) => (await json(await fetch(`${API}/documents/${id}`))).data ?? null;
export const urlFichierPasserelle = (id) => `${API}/documents/${id}/fichier`;
export const retraiterDocument = (id) => fetch(`${API}/documents/${id}/retraiter`, { method: 'POST' }).then(json);

export const fetchExceptionsPasserelle = async (etat = 'ouverte') =>
  (await json(await fetch(`${API}/exceptions?etat=${etat}`))).data ?? [];
export const resoudreException = (id, payload) =>
  fetch(`${API}/exceptions/${id}/resoudre`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }).then(json);

export const fetchPiecesPasserelle = async () => (await json(await fetch(`${API}/pieces`))).data ?? [];
export const fetchObligationsPasserelle = async () => (await json(await fetch(`${API}/obligations`))).data ?? [];
export const fetchJournalPasserelle = async () => (await json(await fetch(`${API}/journal`))).data ?? [];
export const fetchJobsPasserelle = async () => (await json(await fetch(`${API}/jobs`))).data ?? [];

export const initialiserPasserelle = () => fetch(`${API}/initialiser`, { method: 'POST' }).then(json);
export const reprendreErp = () => fetch(`${API}/migration/reprise`, { method: 'POST' }).then(json);

// ───────── Phase 4 : supervision Drive, miroir et sauvegardes ─────────
export const fetchSupervision = async () => (await json(await fetch(`${API}/supervision`))).data ?? null;
export const fetchJournalSync = async (sens, etat) => {
  const q = new URLSearchParams();
  if (sens) q.set('sens', sens);
  if (etat) q.set('etat', etat);
  return (await json(await fetch(`${API}/supervision/journal?${q}`))).data ?? [];
};
export const testerConnexionDrive = (dossierId) =>
  fetch(`${API}/drive/diagnostic${dossierId ? `?dossier_id=${encodeURIComponent(dossierId)}` : ''}`, { method: 'POST' }).then(json);
export const recetteDrive = () => fetch(`${API}/drive/recette`, { method: 'POST' }).then(json);
export const synchroniserMaintenant = () => fetch(`${API}/supervision/synchroniser`, { method: 'POST' }).then(json);
export const sauvegarderMaintenant = () => fetch(`${API}/supervision/sauvegarder`, { method: 'POST' }).then(json);
export const exporterMiroir = () => fetch(`${API}/supervision/miroir`, { method: 'POST' }).then(json);
export const exporterJournal = () => fetch(`${API}/supervision/journal-export`, { method: 'POST' }).then(json);
export const relancerEchecs = () => fetch(`${API}/supervision/relancer`, { method: 'POST' }).then(json);
export const enregistrerIdentifiantsOAuth = (client_id, client_secret) =>
  fetch(`${API}/drive/oauth/identifiants`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ client_id, client_secret }),
  }).then(json);
export const obtenirUrlConsentement = async () =>
  json(await fetch(`${API}/drive/oauth/url?origine=${encodeURIComponent(window.location.origin)}`));
export const revoquerOAuth = () => fetch(`${API}/drive/oauth/revoquer`, { method: 'POST' }).then(json);
export const fetchExportsMiroir = async () => (await json(await fetch(`${API}/miroir/exports`))).data ?? [];
export const fetchSauvegardes = async () => (await json(await fetch(`${API}/sauvegardes`))).data ?? [];
export const fetchManifeste = async (id) => (await json(await fetch(`${API}/sauvegardes/${id}/manifeste`))).data ?? null;
export const testerRestauration = (id) =>
  fetch(`${API}/sauvegardes/test-restauration${id ? `?backup_id=${id}` : ''}`, { method: 'POST' }).then(json);

// ───────── Phase 4 : lectures canoniques utilisées par les écrans métier ─────────
export const fetchDepensesCanoniques = async (du, au) => {
  const q = new URLSearchParams();
  if (du) q.set('du', du);
  if (au) q.set('au', au);
  return (await json(await fetch(`${API}/canonique/depenses?${q}`))).data ?? { lignes: [], totaux: {}, avances_non_affectees: [] };
};
export const fetchFournisseursCanoniques = async () => (await json(await fetch(`${API}/canonique/fournisseurs`))).data ?? { comptes: [] };
export const fetchBanqueCanonique = async () => (await json(await fetch(`${API}/canonique/banque`))).data ?? { transactions: [], extraits: [], synthese: {} };
export const fetchPrevisionnelCanonique = async (jusquAu) =>
  (await json(await fetch(`${API}/canonique/previsionnel${jusquAu ? `?jusqu_au=${jusquAu}` : ''}`))).data ?? { lignes: [], decaissements_non_affectes: [] };
export const fetchFinancementsCanoniques = async () => (await json(await fetch(`${API}/canonique/financements`))).data ?? { contrats: [] };
export const fetchRetenuesCanoniques = async () => (await json(await fetch(`${API}/canonique/retenues`))).data ?? { certificats: [] };
export const saisirFactureCanonique = (payload) =>
  fetch(`${API}/canonique/facture`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }).then(json);
export const enregistrerReglementCanonique = (payload) =>
  fetch(`${API}/canonique/reglement`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }).then(json);

export const ETATS_TACHE_LIBELLE = {
  pending: 'En attente',
  running: 'En cours',
  retry_wait: 'En attente de relance',
  succeeded: 'Terminée',
  failed: 'En échec',
  configuration_required: 'Configuration requise',
};

export const fetchRapprochements = async () => (await json(await fetch(`${API}/rapprochements`))).data ?? { transactions: [], affectations: [], extraits: [] };
export const candidatsTransaction = async (id) => (await json(await fetch(`${API}/rapprochements/${id}/candidats`))).data ?? null;
export const validerRapprochement = (id, payment_id) =>
  fetch(`${API}/rapprochements/${id}/valider`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ payment_id, auteur: 'gérant' }) }).then(json);
export const rejeterTransaction = (id, motif) =>
  fetch(`${API}/rapprochements/${id}/rejet`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ motif, auteur: 'gérant' }) }).then(json);

export const TYPES_PIECE_LIBELLE = {
  facture: 'Facture',
  avoir: 'Avoir',
  certificat_rs: 'Certificat de retenue à la source',
  decharge: 'Décharge de paiement / avance',
  declaration_fiscale: 'Déclaration fiscale',
  quittance: 'Quittance',
  appel_cnss: 'Appel de cotisation CNSS',
  cheque: 'Chèque',
  traite: 'Traite',
  talon: 'Talon',
  contrat_leasing: 'Contrat de leasing',
  echeancier: 'Échéancier',
  extrait_bancaire: 'Extrait bancaire',
  inconnu: 'Pièce à qualifier',
};

export const MOTIFS_EXCEPTION = {
  tiers_a_affecter: 'Tiers à affecter',
  identite_contradictoire: 'Identité contradictoire',
  doublon_probable: 'Doublon probable',
  controles_en_echec: 'Champs illisibles',
  type_non_reconnu: 'Nature de pièce à qualifier',
};

export const ETATS_DOCUMENT = {
  recu: 'Reçu',
  extrait: 'Extrait',
  traite: 'Traité automatiquement',
  exception: 'Exception à résoudre',
};
